import { FEATURE } from "@/features";
import { Button } from "@components/ui/button";
import { Feature } from "@components/Feature";
import {
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemActions,
} from "@components/ui/item";
import { Trans } from "@lingui/react/macro";
import { MessageSquareIcon } from "lucide-react";
import { SEARCH_KIND, FILTER } from "@/constants";
import { ROUTES } from "@/routes_new";
import { formatDateForApi, stringifyQuery } from "@/utils";
import { MENTION_VIEW_DATE_RANGES } from "@/views/MentionsView/MentionLayout/ranges";
import { useMemo } from "react";
import { generatePath } from "react-router-dom";
import { Search } from "@/types/Search";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";

export default function Mentions({
  filters,
  search,
}: {
  search: Nullable<Search>;
  filters: SearchFilterV4;
}) {
  // Helpers
  const stringifiedFilters = useMemo(() => {
    // The default for the mentions view is set in a constant,
    // However the public view does not know of this.
    // Therefore pass along the defaults if nothing's set from
    // the URL.
    let _filters = {};

    if (search?.kind !== SEARCH_KIND.SAVED) {
      const defaultRange = MENTION_VIEW_DATE_RANGES.find(
        (range) => range.default
      );
      if (defaultRange) {
        _filters = {
          [FILTER.START_DATE]: defaultRange.startDate
            ? formatDateForApi(defaultRange.startDate)
            : null,
          [FILTER.END_DATE]: defaultRange.endDate
            ? formatDateForApi(defaultRange.endDate)
            : null,
        };
      }
    }

    return stringifyQuery({ ..._filters, ...filters });
  }, [filters, search]);

  const excelUrl =
    search &&
    `${generatePath(ROUTES.SEARCH_EXPORT_EXCEL, {
      search_uuid: search.uuid,
    })}?${stringifiedFilters}`;

  const csvUrl =
    search &&
    `${generatePath(ROUTES.SEARCH_EXPORT_CSV, {
      search_uuid: search.uuid,
    })}?${stringifiedFilters}`;

  return (
    <Feature required={FEATURE.HAS_EXCEL_EXPORT_FEATURE}>
      {(excelUrl || csvUrl) && (
        <Item variant="outline" size="sm">
          <ItemMedia variant="icon">
            <MessageSquareIcon />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>
              <Trans>Omnämnanden</Trans>
            </ItemTitle>
          </ItemContent>
          <ItemActions>
            {excelUrl && (
              <Button
                asChild
                size="sm"
                variant="outline"
                data-testid="excel-export-link"
                onClick={() => {
                  postTrackingData(EVENT.EXPORT_MENTIONS_TO_EXCEL);
                }}
              >
                <a href={excelUrl}>
                  <Trans>Exportera till Excel</Trans>
                </a>
              </Button>
            )}

            {csvUrl && (
              <Button
                asChild
                size="sm"
                onClick={() => {
                  postTrackingData(EVENT.EXPORT_MENTIONS_TO_CSV);
                }}
              >
                <a href={csvUrl}>
                  <Trans>Exportera till CSV</Trans>
                </a>
              </Button>
            )}
          </ItemActions>
        </Item>
      )}
    </Feature>
  );
}
